import isWithinInterval from "date-fns/isWithinInterval";
import isSameDay from "date-fns/isSameDay";
import getDate from "date-fns/getDate";
import format from "date-fns/format";
import differenceInDays from "date-fns/differenceInDays";
import addMonths from "date-fns/addMonths";
import addDays from "date-fns/addDays";
import classNames from "components/ui/classNames";
import ptBR from "date-fns/locale/pt-BR";
import capitalize from "lodash/fp/capitalize";
import React, { useEffect, useMemo, useState } from "react";
import { adjustToNextMonth, adjustToPreviousMonth, createViewOfMonthDays } from "./utils";

interface IMonthViewCalendarProps {
  startDate: Date;
  endDate: Date;
  onChange: (range: { startDate: Date; endDate: Date }) => void;
}

export default function MonthViewCalendar({ startDate, endDate, onChange }: IMonthViewCalendarProps) {
  const [range, setRange] = useState<Date>(endDate ?? startDate);
  const [calendarMonth, setCalendarMonth] = useState<Date>(startDate);
  const { days, maxDayPossible, minDayPossible } = useMemo(() => createViewOfMonthDays(calendarMonth), [calendarMonth]);

  const nameOfCurrentMonthCalendar = capitalize(
    format(calendarMonth, "MMMM", {
      locale: ptBR,
    }),
  );

  useEffect(() => {
    setCalendarMonth(startDate);
  }, [startDate]);

  useEffect(() => {
    setRange(endDate ?? startDate);
  }, [endDate, startDate]);

  return (
    <div className="text-center">
      <div className="flex items-center text-gray-900">
        <button
          type="button"
          className="-m-1.5 flex flex-none cursor-pointer items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 disabled:cursor-auto disabled:text-gray-200 disabled:hover:text-gray-200"
          onClick={() => setCalendarMonth(adjustToPreviousMonth)}
          disabled={addMonths(calendarMonth, -1) < minDayPossible}
        >
          <span className="sr-only">Mês anterior</span>
          <ChevronLeftIcon className="h-5 w-5" aria-hidden="true" />
        </button>

        <div className="flex-auto space-x-1 font-aeonik font-semibold">
          <span>{nameOfCurrentMonthCalendar}</span>
          <span>{`/ ${calendarMonth.getFullYear()}`}</span>
        </div>

        <button
          type="button"
          className="-m-1.5 flex flex-none cursor-pointer items-center justify-center p-1.5 text-gray-400 hover:text-gray-500 disabled:cursor-auto disabled:text-gray-200 disabled:hover:text-gray-200"
          onClick={() => setCalendarMonth(adjustToNextMonth)}
          disabled={addMonths(calendarMonth, 1) > maxDayPossible}
        >
          <span className="sr-only">Próximo mês</span>
          <ChevronRightIcon className="h-5 w-5" aria-hidden="true" />
        </button>
      </div>

      <div className="mt-6 grid grid-cols-7 text-xs leading-6 text-gray-500">
        <div>S</div>
        <div>T</div>
        <div>Q</div>
        <div>Q</div>
        <div>S</div>
        <div>S</div>
        <div>D</div>
      </div>

      <div
        className="isolate mt-2 grid grid-cols-7 gap-px rounded-lg bg-gray-200 text-sm shadow ring-1 ring-gray-200"
        onMouseLeave={() => setRange(endDate ?? startDate)}
      >
        {days.map((day, dayIndex) => {
          const isSelected = isSameDay(startDate, day.date) || (endDate && isSameDay(endDate, day.date));
          const isRange = isWithinInterval(day.date, {
            start: startDate,
            end: endDate,
          });
          const isHoverRange = isWithinInterval(day.date, {
            start: range < startDate ? range : startDate,
            end: range > startDate ? range : startDate,
          });
          const enableHoverRange = isSameDay(startDate, endDate);
          const isTodayWithinRange = day.isToday && isRange;

          const outsideDayClassNames = classNames({
            "bg-white": !day.isOutsideDay,
            "bg-gray-50": day.isOutsideDay,
            "text-gray-400": day.isOutsideDay && !isSelected && !day.isToday && !day.isCurrentWeek && !day.isNextWeek,
          });

          const commonCalendarClassNames = classNames("py-1.5 focus:z-10", {
            "text-gray-900": !isSelected && !day.isOutsideDay && !day.isToday,
            "rounded-tl-lg": dayIndex === 0,
            "rounded-tr-lg": dayIndex === 6,
            "rounded-bl-lg": dayIndex === days.length - 7,
            "rounded-br-lg": dayIndex === days.length - 1,
          });

          const todayOrSelectedClassNames = classNames({
            "bg-blue-50": day.isToday && !isTodayWithinRange,
            "text-blue-600": day.isToday && !isSelected && !isTodayWithinRange,
            "font-semibold": isSelected || (day.isToday && !isTodayWithinRange),
            "text-white": isSelected,
          });

          const rangeClassNames = classNames({
            "bg-violet-50": isRange || isSelected || (enableHoverRange && isHoverRange),
          });

          return (
            <button
              key={day.date.toDateString()}
              type="button"
              className={classNames(
                outsideDayClassNames,
                commonCalendarClassNames,
                todayOrSelectedClassNames,
                rangeClassNames,
              )}
              onMouseEnter={() => {
                const differenceRangeInDays = differenceInDays(day.date, startDate);

                if (differenceRangeInDays > 31) {
                  setRange(addDays(startDate, 31));
                  return;
                }

                if (differenceRangeInDays < -31) {
                  setRange(addDays(startDate, -31));
                } else {
                  setRange(day.date);
                }
              }}
              onClick={() => {
                // If the user hasn't selected a range, then he's clicking on a single day
                if (!range) {
                  onChange({
                    startDate: day.date,
                    endDate: day.date,
                  });
                  return;
                }

                // If there's a start and an end date and they aren't the same day
                if (startDate && endDate && !isSameDay(startDate, endDate)) {
                  onChange({
                    startDate: day.date,
                    endDate: day.date,
                  });
                } else if (day.date < startDate) {
                  onChange({
                    startDate: day.date,
                    endDate: startDate,
                  });
                } else {
                  onChange({
                    startDate,
                    endDate: day.date,
                  });
                }
              }}
              disabled={day.isOutsideDayOfLimitRange}
            >
              <time
                dateTime={day.date.toDateString()}
                className={classNames("mx-auto flex h-8 w-8 items-center justify-center font-normal text-gray-500", {
                  "rounded-full bg-violet-100 text-violet-700": isSelected,
                  "font-bold": isSelected || day.isToday,
                })}
              >
                {getDate(day.date)}
              </time>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function ChevronLeftIcon(props: React.SVGProps<any>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6" {...props}>
      <path
        fillRule="evenodd"
        d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}

function ChevronRightIcon(props: React.SVGProps<any>) {
  return (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="h-6 w-6" {...props}>
      <path
        fillRule="evenodd"
        d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z"
        clipRule="evenodd"
      />
    </svg>
  );
}
